import { isUUIDV4, Queries } from '@aline/common';
import type { Core } from '@pdftron/webviewer';
import { pdfTronCore } from 'src/components/viewer/viewer-context';
import type { SelectedFileReference } from 'src/editor';

export function getIsAlineFieldName(fieldName: string) {
  if (isUUIDV4(fieldName)) return true;
  return Queries.isCheckboxOptionCompositeFieldName(fieldName);
}

/**
 * Drops `<widget>` annotations for the uploaded PDF's own form fields, leaving the `<fields>`
 * value tree untouched.
 */
export function stripPdfOwnWidgetAnnots(xfdf: string | null | undefined): string {
  if (!xfdf) return '';

  const doc = new DOMParser().parseFromString(xfdf, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) return xfdf;

  const pdfOwnWidgets = Array.from(doc.getElementsByTagName('widget')).filter(widget => {
    const fieldName = widget.getAttribute('field');
    if (!fieldName) return false;
    return !getIsAlineFieldName(fieldName);
  });
  if (pdfOwnWidgets.length === 0) return xfdf;

  pdfOwnWidgets.forEach(widget => widget.remove());
  return new XMLSerializer().serializeToString(doc);
}

interface PDFWindowCoords {
  x: number;
  y: number;
}

function getPDFWindowCoords(
  documentViewer: Core.DocumentViewer,
  event: Pick<MouseEvent, 'clientX' | 'clientY'>
): PDFWindowCoords | null {
  const scrollElement = documentViewer.getScrollViewElement();
  if (!scrollElement) return null;

  const { height, left, top, width } = scrollElement.getBoundingClientRect();
  const scrollLeft = scrollElement.scrollLeft ?? 0;
  const scrollTop = scrollElement.scrollTop ?? 0;
  const topWithinElement = event.clientY - top;
  const leftWithinElement = event.clientX - left;
  const outsideXBounds = leftWithinElement < 0 || leftWithinElement > width;
  const outsideYBounds = topWithinElement < 0 || topWithinElement > height;
  if (outsideXBounds || outsideYBounds) return null;

  const documentRoot = document.documentElement.getBoundingClientRect();
  return {
    x: event.clientX - documentRoot.left + scrollLeft,
    y: event.clientY - documentRoot.top + scrollTop,
  };
}

interface PDFPageCoords {
  x: number;
  y: number;
}

export interface PDFPageCoordsInfo {
  eventPageNum: number;
  pageCoords: PDFPageCoords;
  windowCoords: PDFWindowCoords;
}

export enum ResolveToPageLocation {
  ClosestPageLocation = 'closest-page-location',
  LocationInPage = 'location-in-page',
}

export function getPDFPageCoords(
  documentViewer: Core.DocumentViewer,
  options: {
    event: Pick<MouseEvent, 'clientX' | 'clientY'>;
    resolveTo?: ResolveToPageLocation;
  }
): PDFPageCoordsInfo | null {
  const { event, resolveTo = ResolveToPageLocation.ClosestPageLocation } = options;
  const windowCoords = getPDFWindowCoords(documentViewer, event);
  if (!windowCoords) return null;

  const displayMode = documentViewer.getDisplayModeManager().getDisplayMode();
  const selectedPages = displayMode.getSelectedPages(windowCoords, windowCoords);
  const eventPageNum =
    selectedPages.first !== null ? selectedPages.first : documentViewer.getCurrentPage();
  const pageCoords = displayMode.windowToPage(windowCoords, eventPageNum);
  const pageWidth = documentViewer.getPageWidth(eventPageNum);
  const pageHeight = documentViewer.getPageHeight(eventPageNum);
  const outOfXBounds = pageCoords.x < 0 || pageCoords.x > pageWidth;
  const outOfYBounds = pageCoords.y < 0 || pageCoords.y > pageHeight;
  const outOfPageBounds = outOfXBounds || outOfYBounds;
  if (resolveTo === ResolveToPageLocation.LocationInPage && outOfPageBounds) return null;

  const closestPageXLocation = Math.max(0, Math.min(pageWidth, pageCoords.x));
  const closestPageYLocation = Math.max(0, Math.min(pageHeight, pageCoords.y));

  return {
    eventPageNum,
    pageCoords: { x: closestPageXLocation, y: closestPageYLocation },
    windowCoords,
  };
}

export function getFileReferenceQuad(args: {
  aiReference: SelectedFileReference;
  documentViewer: Core.DocumentViewer;
}): Core.Math.Quad {
  const { aiReference, documentViewer } = args;
  const [left = 0, top = 0, right = 0, bottom = 0] = aiReference.rect;
  const aiReferenceQuad = new pdfTronCore.Math.Quad(
    left,
    bottom,
    right,
    bottom,
    right,
    top,
    left,
    top
  );
  const pageWidth = documentViewer.getPageWidth(aiReference.pageNum);
  const pageHeight = documentViewer.getPageHeight(aiReference.pageNum);
  const pageRotation = documentViewer.getCompleteRotation(aiReference.pageNum);
  const x1 = aiReferenceQuad.x1;
  const x2 = aiReferenceQuad.x2;
  const x3 = aiReferenceQuad.x3;
  const x4 = aiReferenceQuad.x4;
  const y1 = aiReferenceQuad.y1;
  const y2 = aiReferenceQuad.y2;
  const y3 = aiReferenceQuad.y3;
  const y4 = aiReferenceQuad.y4;

  switch (pageRotation) {
    case pdfTronCore.PageRotation.E_90:
      return new pdfTronCore.Math.Quad(
        y1,
        pageHeight - x1,
        y2,
        pageHeight - x2,
        y3,
        pageHeight - x3,
        y4,
        pageHeight - x4
      );
    case pdfTronCore.PageRotation.E_180:
      return new pdfTronCore.Math.Quad(
        pageWidth - x3,
        pageHeight - y3,
        pageWidth - x4,
        pageHeight - y4,
        pageWidth - x1,
        pageHeight - y1,
        pageWidth - x2,
        pageHeight - y2
      );
    case pdfTronCore.PageRotation.E_270:
      return new pdfTronCore.Math.Quad(
        pageWidth - y3,
        x1,
        pageWidth - y4,
        x2,
        pageWidth - y1,
        x3,
        pageWidth - y2,
        x4
      );
    case pdfTronCore.PageRotation.E_0:
    default:
      return aiReferenceQuad;
  }
}
